/* eslint-disable no-unused-vars */
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import React from 'react';
import { dispatchPipelineStage, promoteReleaseArtifact } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';
import {
  formatArtifactSummary,
  formatReleaseAppVersionLabel,
} from '../../utils/formatters';

// v1 simplification: skillora-deploy is hardcoded rather than resolved via
// connector_resource_mappings, since that table has no "purpose" tag yet to
// distinguish a deploy repo from a source repo. Matches this phase's own
// stated approach of proving the model against Skillora's real shape before
// generalizing (see cd-daily-workflow.md §9).
const STAGING_DEPLOY_REPO = 'nerds-cooking/skillora-deploy';
const STAGING_DEPLOY_WORKFLOW = 'deploy.yml';

type DeployCheckState =
  | { phase: 'idle' }
  | { phase: 'checking' }
  | { phase: 'error'; message: string }
  | {
      phase: 'checked';
      conclusion: string | null;
      status: string;
      runUrl?: string;
      isNewerThanArtifact: boolean;
    };

type PromotionDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function PromotionDialog({ actionDialogReturn, onNavigate: _onNavigate }: PromotionDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const {
    workspace,
    releaseAppVersionById,
    releaseAppById,
    sortedArtifacts,
    artifactById,
    visibleEnvironments,
    setWorkspaceVersion,
  } = useWorkspace();
  const { releaseAppVersions, channels } = workspace;

  // Deliberately every version across every app (not just the currently
  // "active" app in the workspace switcher) — a bundle is a single
  // platform-agnostic OTA artifact that can be promoted onto any app's
  // lane, so requiring the user to first go switch the active app on
  // another page before they can pick its version here is just friction.
  const releaseVersionOptionsByPlatform = React.useMemo(() => {
    const groups = new Map<string, typeof releaseAppVersions>();
    for (const version of releaseAppVersions) {
      const platform = releaseAppById.get(version.releaseAppId)?.platform || 'other';
      const existing = groups.get(platform);
      if (existing) {
        existing.push(version);
      } else {
        groups.set(platform, [version]);
      }
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [releaseAppVersions, releaseAppById]);

  const [deployCheck, setDeployCheck] = React.useState<DeployCheckState>({ phase: 'idle' });

  const dialogEnvironment =
    actionDialog?.kind === 'promotion' ? actionDialog.environment : undefined;
  const dialogArtifactId =
    actionDialog?.kind === 'promotion' ? actionDialog.releaseArtifactId : undefined;

  // Stale check results from a previous selection would be actively
  // misleading here, so any change to the target resets it.
  React.useEffect(() => {
    setDeployCheck({ phase: 'idle' });
  }, [dialogEnvironment, dialogArtifactId]);

  if (!actionDialog || actionDialog.kind !== 'promotion') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const promotionVersion = releaseAppVersionById.get(actionDialog.releaseAppVersionId);
  const promotionApp = promotionVersion
    ? releaseAppById.get(promotionVersion.releaseAppId)
    : undefined;
  const exactTarget = promotionVersion
    ? channels.find(
        (channel) =>
          channel.environment === actionDialog.environment &&
          channel.releaseAppVersionId === promotionVersion.releaseAppVersionId
      )
    : undefined;
  const selectedArtifact = actionDialog.releaseArtifactId
    ? artifactById.get(actionDialog.releaseArtifactId)
    : undefined;
  const currentArtifact = exactTarget?.releaseArtifactId
    ? artifactById.get(exactTarget.releaseArtifactId)
    : undefined;
  const bundleAlreadyRunning = Boolean(
    exactTarget?.releaseArtifactId &&
      exactTarget.releaseArtifactId === actionDialog.releaseArtifactId
  );

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'promotion' || dialogBusy) return;
    if (!actionDialog.environment) {
      setDialogError('Select an environment before deploying.');
      return;
    }
    setDialogBusy(true);
    setDialogError('');
    try {
      if (actionDialog.releaseArtifactId) {
        const selectedVersion = releaseAppVersionById.get(actionDialog.releaseAppVersionId);
        if (!selectedVersion) {
          setDialogError('Select an app store version before promoting.');
          return;
        }
        if (!exactTarget) {
          setDialogError(
            'This app version does not have an exact release target in the selected environment.'
          );
          return;
        }
        await promoteReleaseArtifact(
          session.accessToken,
          actionDialog.releaseArtifactId,
          {
            releaseAppVersionId: selectedVersion.releaseAppVersionId,
            environment: actionDialog.environment,
            channel: selectedVersion.version
          }
        );
        setWorkspaceVersion((v) => v + 1);
      }
      toast('success', 'Bundle deployed');
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  async function checkStagingDeploy() {
    if (!session || !selectedArtifact) return;
    setDeployCheck({ phase: 'checking' });
    try {
      const run = await dispatchPipelineStage(session.accessToken, {
        connectorType: 'github',
        connectorAction: 'await_workflow_run',
        actionConfig: {
          repo: STAGING_DEPLOY_REPO,
          workflowFileName: STAGING_DEPLOY_WORKFLOW,
          branch: 'main',
        },
      });
      const payload = run.responsePayload as
        | {
            found?: boolean;
            status?: string;
            conclusion?: string | null;
            runUrl?: string;
            runUpdatedAt?: string;
          }
        | undefined;
      if (!payload?.found) {
        setDeployCheck({
          phase: 'error',
          message: 'No staging deploy run found on skillora-deploy main.',
        });
        return;
      }
      const artifactCreated = selectedArtifact.created
        ? new Date(selectedArtifact.created).getTime()
        : 0;
      const runUpdated = payload.runUpdatedAt
        ? new Date(payload.runUpdatedAt).getTime()
        : 0;
      setDeployCheck({
        phase: 'checked',
        status: payload.status || 'unknown',
        conclusion: payload.conclusion ?? null,
        runUrl: payload.runUrl,
        // Heuristic, not a precise per-commit match — see
        // getLatestWorkflowRun's own doc comment. Good enough to catch
        // "you're about to promote before staging even finished deploying."
        isNewerThanArtifact: runUpdated > 0 && runUpdated >= artifactCreated,
      });
    } catch (err: any) {
      setDeployCheck({
        phase: 'error',
        message: err?.response?.data?.message || err?.message || 'Check failed.',
      });
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form
        className="dialogPanel deploymentDialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promotion-dialog-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Deployment</div>
            <h3 id="promotion-dialog-title">Deploy bundle</h3>
            <p>Assign one bundle to one exact platform, app version, and environment.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm deploymentPlanner">
          <section className="deploymentPlannerStep" aria-labelledby="deployment-target-heading">
            <div className="deploymentPlannerStepHeader">
              <span>1</span>
              <div>
                <strong id="deployment-target-heading">Choose the exact target</strong>
                <small>The app version and environment identify one delivery lane.</small>
              </div>
            </div>
            <div className="deploymentPlannerTargetGrid">
              <label className="settingsField">
                <span>App and store version</span>
                <select
                  value={actionDialog.releaseAppVersionId}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      releaseAppVersionId: e.target.value,
                    })
                  }
                >
                  <option value="">Choose app and version…</option>
                  {releaseVersionOptionsByPlatform.map(([platform, versions]) => (
                    <optgroup key={platform} label={platform.toUpperCase()}>
                      {versions.map((version) => (
                        <option
                          key={version.releaseAppVersionId}
                          value={version.releaseAppVersionId}
                        >
                          {formatReleaseAppVersionLabel(version, releaseAppById)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label className="settingsField">
                <span>Environment</span>
                <select
                  value={actionDialog.environment}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      environment: e.target.value as
                        | ''
                        | 'dev'
                        | 'staging'
                        | 'production'
                        | 'internal',
                    })
                  }
                >
                  <option value="">Choose environment…</option>
                  {visibleEnvironments.map((environment) => (
                    <option
                      key={environment.tenantEnvironmentId}
                      value={environment.slug}
                    >
                      {environment.name}
                    </option>
                  ))}
                </select>
                {visibleEnvironments.length === 0 ? (
                  <span className="fieldHint">
                    Show an environment from the Environments page before deploying.
                  </span>
                ) : null}
              </label>
            </div>
          </section>

          <section className="deploymentPlannerStep" aria-labelledby="deployment-bundle-heading">
            <div className="deploymentPlannerStepHeader">
              <span>2</span>
              <div>
                <strong id="deployment-bundle-heading">Choose what will run</strong>
                <small>The bundle selected from the registry remains visible here.</small>
              </div>
            </div>
            <label className="settingsField">
              <span>Bundle</span>
              <select
                value={actionDialog.releaseArtifactId}
                onChange={(e) =>
                  setActionDialog({
                    ...actionDialog,
                    releaseArtifactId: e.target.value,
                  })
                }
              >
                <option value="">Choose bundle…</option>
                {sortedArtifacts.map((artifact) => (
                  <option
                    key={artifact.releaseArtifactId}
                    value={artifact.releaseArtifactId}
                  >
                    {formatArtifactSummary(artifact)}
                  </option>
                ))}
              </select>
            </label>
          </section>
          {promotionVersion && actionDialog.environment ? (
            <>
              <div className="releaseConfirmation" aria-label="Deployment summary">
                <div className="releaseConfirmationRow">
                  <span>Exact target</span>
                  <strong>
                    {promotionApp?.name || 'Unknown app'} ·{' '}
                    {promotionApp?.platform?.toUpperCase() || 'Unavailable'} ·{' '}
                    {promotionVersion.version} · {actionDialog.environment}
                  </strong>
                </div>
                <div className="releaseConfirmationRow">
                  <span>Currently running</span>
                  <strong>
                    {currentArtifact
                      ? formatArtifactSummary(currentArtifact)
                      : exactTarget
                        ? 'No bundle'
                        : 'Target unavailable'}
                  </strong>
                </div>
                <div className="releaseConfirmationRow releaseConfirmationRow--change">
                  <span>New bundle</span>
                  <strong>
                    {selectedArtifact
                      ? `${formatArtifactSummary(selectedArtifact)}${
                          bundleAlreadyRunning ? ' · already running' : ''
                        }`
                      : 'Choose a bundle'}
                  </strong>
                </div>
              </div>
              {!exactTarget ? (
                <p className="fieldError">
                  No exact target exists for this app version and environment. Create the
                  target before deploying.
                </p>
              ) : null}
              {actionDialog.environment === 'staging' && selectedArtifact ? (
                <div className="releaseConfirmation" aria-label="Staging deploy status">
                  <div className="releaseConfirmationRow">
                    <span>Staging deploy (skillora-deploy)</span>
                    {deployCheck.phase === 'idle' ? (
                      <button
                        type="button"
                        className="secondaryButton"
                        onClick={() => void checkStagingDeploy()}
                      >
                        Check status
                      </button>
                    ) : deployCheck.phase === 'checking' ? (
                      <strong className="releaseConfirmationRow--pending">
                        <Loader2 size={14} className="spin" /> Checking…
                      </strong>
                    ) : deployCheck.phase === 'error' ? (
                      <strong className="fieldError">{deployCheck.message}</strong>
                    ) : deployCheck.conclusion === 'success' && deployCheck.isNewerThanArtifact ? (
                      <strong className="releaseConfirmationRow--ok">
                        <CheckCircle2 size={14} /> Latest deploy succeeded, after this bundle was
                        built
                      </strong>
                    ) : (
                      <strong className="fieldError">
                        <AlertTriangle size={14} /> Latest deploy: {deployCheck.status}
                        {deployCheck.conclusion ? ` (${deployCheck.conclusion})` : ''}
                        {!deployCheck.isNewerThanArtifact
                          ? ' — older than this bundle, may not reflect it'
                          : ''}
                      </strong>
                    )}
                  </div>
                  <p className="fieldHint">
                    Heuristic check against skillora-deploy&apos;s latest run on main — not a precise
                    per-commit match. Confirm staging actually looks right before deploying OTA
                    on top of it.
                  </p>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button
            type="submit"
            className="primaryButton"
            disabled={
              dialogBusy ||
              !actionDialog.releaseArtifactId ||
              !actionDialog.releaseAppVersionId ||
              !actionDialog.environment ||
              visibleEnvironments.length === 0 ||
              !exactTarget ||
              bundleAlreadyRunning
            }
          >
            {dialogBusy
              ? 'Deploying…'
              : promotionApp && promotionVersion
                ? `Deploy to ${promotionApp.name} ${promotionVersion.version}`
                : 'Deploy bundle'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { PromotionDialog };
